import { Component, type ReactNode } from 'react';
import { AlertIcon as AlertTriangle } from './ui/Icons';

interface Props {
  children: ReactNode;
  inline?: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('ErrorBoundary caught:', error, errorInfo);
  }

  private renderActions() {
    return (
      <div className="flex flex-col gap-2">
        <button
          onClick={() => window.location.reload()}
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-hover transition-[var(--transition-fast)]"
        >
          Reload Page
        </button>
        <button
          onClick={() => {
            // The Layout listens for this event and opens the feedback
            // modal pre-filled with the error (ErrorBoundary is a class
            // component and cannot use hooks directly).
            window.dispatchEvent(
              new CustomEvent('open-feedback', {
                detail: {
                  type: 'bug_report' as const,
                  category: 'bug',
                  error: this.state.error?.message,
                },
              }),
            );
          }}
          className="rounded-md border border-border px-4 py-2 text-sm font-medium text-[var(--color-text)] hover:bg-[var(--color-bg-tertiary)] transition-[var(--transition-fast)]"
        >
          Report this error
        </button>
      </div>
    );
  }

  private renderErrorDetails() {
    return (
      <pre className="mb-4 max-h-32 overflow-auto rounded bg-[var(--color-bg-tertiary)] p-2 text-left text-xs text-[var(--color-text-muted)]">
        {this.state.error?.message}
      </pre>
    );
  }

  render() {
    if (this.state.hasError) {
      if (this.props.inline) {
        return (
          <div className="rounded-lg border border-border bg-surface p-4 text-center">
            <AlertTriangle className="mx-auto mb-2 h-7 w-7 text-warning" aria-hidden="true" />
            <h3 className="mb-1 text-sm font-semibold text-[var(--color-text)]">
              Something went wrong
            </h3>
            <p className="mb-3 text-xs text-[var(--color-text-secondary)]">
              An unexpected error occurred in this panel.
            </p>
            {this.renderErrorDetails()}
            {this.renderActions()}
          </div>
        );
      }
      return (
        <div className="flex min-h-screen items-center justify-center bg-surface-secondary p-8">
          <div className="max-w-md rounded-lg border border-border bg-surface p-8 text-center shadow-lg">
            <AlertTriangle className="mx-auto mb-4 h-12 w-12 text-warning" aria-hidden="true" />
            <h1 className="mb-2 text-xl font-bold text-[var(--color-text)]">
              Something went wrong
            </h1>
            <p className="mb-4 text-sm text-[var(--color-text-secondary)]">
              An unexpected error occurred. Please reload the page to continue.
            </p>
            {this.renderErrorDetails()}
            {this.renderActions()}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
